<template>
  <div class="ct-page">
    <!-- ── Hero header with stats ─────────────────────────── -->
    <div class="ct-hero">
      <div class="ct-hero__left">
        <div class="ct-hero__icon">
          <i class="fa-solid fa-broom"></i>
        </div>
        <div>
          <h1 class="ct-hero__title">{{ msg('Cleaning tasks') }}</h1>
          <p class="ct-hero__subtitle">
            {{ totalTasks }} {{ msg('open tasks across') }} {{ Object.keys(groupedByBuilding).length || 0 }}
            {{ msg('properties') }}
          </p>
        </div>
      </div>
      <div class="ct-hero__stats">
        <div class="ct-stat ct-stat--urgent">
          <div class="ct-stat__num">{{ urgentCount }}</div>
          <div class="ct-stat__lbl">{{ msg('Urgent') }}</div>
        </div>
        <div class="ct-stat ct-stat--progress">
          <div class="ct-stat__num">{{ progressCount }}</div>
          <div class="ct-stat__lbl">{{ msg('In progress') }}</div>
        </div>
        <div class="ct-stat ct-stat--late">
          <div class="ct-stat__num">{{ lateCount }}</div>
          <div class="ct-stat__lbl">{{ msg('Late') }}</div>
        </div>
        <button class="ct-refresh" @click="refresh()" v-b-tooltip :title="msg('refresh')">
          <feather-icon icon="RefreshCcwIcon" size="16"/>
        </button>
      </div>
    </div>

    <!-- ── Filter strip — professional layout ─────────────── -->
    <div class="ct-filters">
      <div class="ct-filters__head">
        <div class="ct-search">
          <feather-icon icon="SearchIcon" size="14" class="ct-search__icon"/>
          <input
              v-model="searchQuery"
              type="text"
              :placeholder="msg('Search by guest, apt, address…')"
              class="ct-search__input"
          />
          <button v-if="searchQuery" class="ct-search__clear" @click="searchQuery = ''">
            <feather-icon icon="XIcon" size="13"/>
          </button>
        </div>
        <div class="ct-filters__chips">
          <button
              v-for="chip in quickChips"
              :key="chip.key"
              class="ct-chip"
              :class="[`ct-chip--${chip.variant}`, { active: quickFilter === chip.key }]"
              @click="quickFilter = quickFilter === chip.key ? null : chip.key"
          >
            <feather-icon :icon="chip.icon" size="12" class="mr-25"/>
            {{ chip.label }}
            <span v-if="chip.count > 0" class="ct-chip__count">{{ chip.count }}</span>
          </button>
        </div>
      </div>
      <div class="ct-filters__body">
        <div class="ct-filter-field">
          <label>
            <feather-icon icon="HomeIcon" size="13" class="mr-25"/>
            {{ msg('Building') }}
          </label>
          <building-select-box multiple v-model="filter.buildingIds" @input="refresh"/>
        </div>
        <div class="ct-filter-field">
          <label>
            <feather-icon icon="LayersIcon" size="13" class="mr-25"/>
            {{ msg('Real estate') }}
          </label>
          <real-estate-picker multiple v-model="filter.realEstatesIds" @input="refresh"/>
        </div>
        <button v-if="hasActiveFilters" class="ct-clear-btn" @click="clearAllFilters" v-b-tooltip :title="msg('Clear all filters')">
          <feather-icon icon="XIcon" size="13" class="mr-25"/>
          {{ msg('Reset') }}
        </button>
      </div>
    </div>

    <!-- ── Loading ──────────────────────────────────────────── -->
    <div v-if="loading" class="ct-loading">
      <b-spinner variant="primary" small class="mr-50"/>
      <span>{{ msg('Loading') }}…</span>
    </div>

    <!-- ── Empty state ──────────────────────────────────────── -->
    <div v-else-if="Object.keys(groupedByBuilding).length === 0" class="ct-empty">
      <div class="ct-empty__icon"><i class="fa-solid fa-broom"></i></div>
      <h4>{{ msg('All clean') }}</h4>
      <p>{{ msg('No cleaning tasks match your filters right now.') }}</p>
    </div>

    <!-- ── Property groups ──────────────────────────────────── -->
    <div v-else class="ct-grid">
      <article
          v-for="b in groupedByBuilding"
          :key="b.id"
          class="ct-group"
      >
        <header class="ct-group__head">
          <div class="ct-group__icon" :class="b.type === 'building' ? 'ct-group__icon--building' : 'ct-group__icon--house'">
            <i class="fa-solid" :class="b.type === 'building' ? 'fa-building' : 'fa-house'"></i>
          </div>
          <div class="ct-group__info">
            <div class="ct-group__addr">{{ b.address }}</div>
            <div class="ct-group__count">
              {{ b.tasks.length }} {{ b.tasks.length === 1 ? msg('task') : msg('tasks') }}
            </div>
          </div>
        </header>

        <div class="ct-tasks">
          <button
              v-for="task in b.tasks"
              :key="task.id"
              class="ct-task"
              :class="[
                `ct-task--${task.urgency}`,
                { 'ct-task--late': task.isCleaningLate, 'ct-task--progress': task.hasStated }
              ]"
              @click="openModal(task.id)"
          >
            <span class="ct-task__stripe"></span>
            <div class="ct-task__head">
              <div class="ct-task__apt-row">
                <span class="ct-task__apt">{{ msg('apt') }} {{ task.realEstate.apt || '—' }}</span>
                <small v-if="task.hasReservation" class="ct-task__rel">
                  <feather-icon icon="ClockIcon" size="11" class="mr-25"/>
                  {{ task.requestedCleanTimeEnd | momentTimeFromNow() }}
                </small>
              </div>
              <b-badge :variant="task.statusDetails.variant" pill class="ct-task__status">
                {{ task.statusDetails.label }}
              </b-badge>
            </div>

            <div class="ct-task__row">
              <feather-icon icon="UserIcon" size="12" class="ct-task__row-icon"/>
              <a
                  :href="`/real_estate/contracts/rent/${task.contractId}`"
                  target="_blank"
                  @click.stop
                  class="ct-task__link"
              >
                <span class="ct-task__client">{{ task.clientName || msg('Unknown') }}</span>
                <span class="ct-task__time"> · {{ task.requestedCleanTime | moment('DD/MM HH:mm', true) }}</span>
              </a>
            </div>

            <div class="ct-task__row" v-if="task.hasReservation">
              <feather-icon icon="ChevronsRightIcon" size="12" class="ct-task__row-icon"/>
              <a
                  :href="`/real_estate/contracts/rent/${task.nextReservationId}`"
                  target="_blank"
                  @click.stop
                  class="ct-task__link"
              >
                <span class="ct-task__client">{{ task.nextReservationName }}</span>
                <span class="ct-task__time"> · {{ task.requestedCleanTimeEnd | moment('DD/MM HH:mm', true) }}</span>
              </a>
            </div>

            <p v-if="task.notes && task.notes.trim() !== ''" class="ct-task__notes">
              <feather-icon icon="MessageSquareIcon" size="11" class="mr-25"/>{{ task.notes }}
            </p>

            <div v-if="task.hasDamage" class="ct-task__alert">
              <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25"/>
              {{ msg('Damage reported') }}
            </div>

            <div class="ct-task__foot">
              <button
                  class="ct-task__btn"
                  @click.stop="openTaskPage(task.id)"
                  v-b-tooltip
                  :title="msg('Open in new tab')"
              >
                <feather-icon icon="ExternalLinkIcon" size="13"/>
              </button>
            </div>
          </button>
        </div>
      </article>
    </div>

    <cleaning-edit-modal ref="cleaningModal" @updated="refresh"/>
  </div>
</template>
<script>

import RealEstatePicker from "@/components/components/picker/realEstatesPicker.vue";
import {mapActions} from "vuex";
import cleaningEditModal from "@/components/modals/cleaning/cleaningEditModal.vue";
import buildingSelectBox from "@/components/components/picker/buildingSelectBox.vue";

const getCleaningFilter = ()=>({
  realEstatesIds: [],
  buildingIds: []
});
export default {
  name: "CleaningTaskView",
  components: {RealEstatePicker,buildingSelectBox,cleaningEditModal},
  data: () => ({
    filter: getCleaningFilter(),
    tasks: [],
    realEstatesMap: {},
    loading: false,
    searchQuery: '',
    quickFilter: null,
  }),
  created() {
    this.refresh()
    this.$root.$on('crm::branch-updated', this.refresh);
  },
  destroyed() {
    this.$root.$off('crm::branch-updated', this.refresh);
  },
  computed:{
    statusEnum() {
      return this.$t('cleaningStatusEnum');
    },
    totalTasks() {
      return this.tasks ? this.tasks.length : 0;
    },
    urgentCount() {
      return this.tasks.filter(t => t.isCleaningRequired && t.isCloseToReservation).length;
    },
    progressCount() {
      return this.tasks.filter(t => t.hasStated).length;
    },
    lateCount() {
      return this.tasks.filter(t => t.isCleaningLate).length;
    },
    todayCount() {
      const $this = this;
      const today = $this.$moment().startOf('day');
      const tomorrow = $this.$moment().endOf('day');
      return this.tasks.filter(t => {
        const d = $this.$moment(t.requestedCleanTime);
        return d.isAfter(today) && d.isBefore(tomorrow);
      }).length;
    },
    hasActiveFilters() {
      return (this.filter.buildingIds && this.filter.buildingIds.length > 0)
          || (this.filter.realEstatesIds && this.filter.realEstatesIds.length > 0)
          || (this.searchQuery && this.searchQuery.trim() !== '')
          || this.quickFilter != null;
    },
    quickChips() {
      return [
        { key: 'today', label: this.msg('Today'), icon: 'CalendarIcon', variant: 'primary', count: this.todayCount },
        { key: 'urgent', label: this.msg('Urgent'), icon: 'AlertCircleIcon', variant: 'warning', count: this.urgentCount },
        { key: 'progress', label: this.msg('In progress'), icon: 'LoaderIcon', variant: 'info', count: this.progressCount },
        { key: 'late', label: this.msg('Late'), icon: 'AlertTriangleIcon', variant: 'danger', count: this.lateCount },
        { key: 'damage', label: this.msg('Damage'), icon: 'ShieldOffIcon', variant: 'danger', count: this.tasks.filter(t => t.hasDamage).length },
      ];
    },
    /** Filtered tasks (search + quick chip) feeding `groupedByBuilding`. */
    filteredTasks() {
      let list = this.tasks || [];
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (q) {
        list = list.filter(t => {
          const re = this.realEstatesMap[t.realEstateId] || {};
          return (
              (t.clientName && t.clientName.toLowerCase().includes(q))
              || (t.notes && t.notes.toLowerCase().includes(q))
              || (re.fullAddress && re.fullAddress.toLowerCase().includes(q))
              || (re.locationAddress && re.locationAddress.toLowerCase().includes(q))
              || (re.apt && String(re.apt).toLowerCase().includes(q))
          );
        });
      }
      if (this.quickFilter === 'today') {
        const today = this.$moment().startOf('day');
        const tomorrow = this.$moment().endOf('day');
        list = list.filter(t => {
          const d = this.$moment(t.requestedCleanTime);
          return d.isAfter(today) && d.isBefore(tomorrow);
        });
      } else if (this.quickFilter === 'urgent') {
        list = list.filter(t => t.isCleaningRequired && t.isCloseToReservation);
      } else if (this.quickFilter === 'progress') {
        list = list.filter(t => t.hasStated);
      } else if (this.quickFilter === 'late') {
        list = list.filter(t => t.isCleaningLate);
      } else if (this.quickFilter === 'damage') {
        list = list.filter(t => t.hasDamage);
      }
      return list;
    },
    groupedByBuilding() {
      if(this.filteredTasks == null) return {};
      let result = {};
      this.filteredTasks.forEach(task=>{
        let realEstate = this.realEstatesMap[task.realEstateId];
        if(realEstate == null) {
          console.warn("Real estate not found", task.realEstateId, this.realEstatesMap)
          return
        }
        let building = task.buildingId || task.realEstateId;
        if(result[building] == null) {
          result[building] = {
            id: building,
            type: task.buildingId == null ? 'realEstate' : 'building',
            address: task.buildingId == null ? realEstate.fullAddress : realEstate.locationAddress,
            tasks: []
          }
        }
        let urgency = 'normal';
        if (task.isCleaningLate) {
          urgency = 'late';
        } else if (task.isCleaningRequired && task.isCloseToReservation) {
          urgency = 'urgent';
        } else if (task.hasStated) {
          urgency = 'progress';
        }
        result[building].tasks.push({...task, realEstate, urgency, statusDetails: this.statusEnum[task.status]});
      });
      return Object.values(result).sort((a,b)=>b.tasks.length - a.tasks.length);
    }
  },
  methods: {
    ...mapActions('cleaning', ['fetchCleaningTasks']),
    refresh() {
      let $this = this;
      this.loading = true;
      this.fetchCleaningTasks(this.filter).then(data=>{
        this.tasks = $this.checkTasks(data.CleaningTasks);
        this.realEstatesMap = (data.RealEstates || []).toObject('id');
      }).catch(()=>{
        $this.$toast.error($this.msg('Error refreshing data'))
      }).finally(()=>{
        $this.loading = false;
      })
    },
    openTaskPage(eventId) {
      window.open(`/cleaning/edit/${eventId}`, '_blank');
    },
    checkTasks(tasks) {
      let $this = this;
      let now = this.$moment();
      return tasks.map(task => {
        task.hasStated = task.status !== "NEED_CLEANING" && task.status !== "CLEANED" && task.status !== "CANCELLED" && task.status !== "OVERDUE";
        task.hasReservation = task.nextReservationId != null;
        if(task.hasReservation){
          task.isCloseToReservation = $this.$moment(task.requestedCleanTimeEnd).isBefore(now.add(3, 'hour'));
          task.isTimeEnded = $this.$moment(task.requestedCleanTimeEnd).isBefore(now);
          task.isCleaningLate = !task.hasStated && task.isTimeEnded && !task.cleaned;
        }
        task.isCleaningRequired = !task.isTimeEnded && $this.$moment(task.requestedCleanTime).isBefore(now);
        return task;
      })
    },
    resetFilters() {
      this.filter = getCleaningFilter();
    },
    clearAllFilters() {
      this.filter = getCleaningFilter();
      this.searchQuery = '';
      this.quickFilter = null;
      this.refresh();
    },
    openModal(taskId) {
       this.$refs.cleaningModal.showById(taskId);
    }
  }
}
</script>


<style scoped lang="scss">
$ct-radius: 14px;
$ct-radius-lg: 18px;
$ct-border: rgba(60, 60, 70, 0.08);
$ct-border-dark: rgba(255, 255, 255, 0.06);
$ct-primary: #7367f0;
$ct-success: #28c76f;
$ct-warning: #ff9f43;
$ct-danger: #ea5455;
$ct-info: #00cfe8;

.ct-page { min-height: 500px; padding-bottom: 24px; }

/* ── Hero ─────────────────────────────────────────────── */
.ct-hero {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.08) 0%, rgba(0, 207, 232, 0.06) 100%);
  border: 1px solid $ct-border;
  border-radius: $ct-radius-lg;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;
  color: var(--text);
}

.ct-hero__title { color: var(--text); }
.ct-hero__addr { color: var(--text); }
.ct-task__apt { color: var(--text); }

.ct-hero__left {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 1;
}

.ct-hero__icon {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: linear-gradient(135deg, $ct-primary 0%, #5e50ee 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 22px rgba(115, 103, 240, 0.32);

  i { font-size: 1.3rem; }
}

.ct-hero__title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.ct-hero__subtitle {
  font-size: 0.85rem;
  color: var(--secondary);
  margin: 2px 0 0 0;
  font-weight: 500;
}

.ct-hero__stats {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ct-stat {
  background: var(--card-bg, white);
  border: 1px solid $ct-border;
  border-radius: 12px;
  padding: 8px 14px;
  text-align: center;
  min-width: 76px;

  &--urgent { border-color: rgba(255, 159, 67, 0.4); .ct-stat__num { color: $ct-warning; } }
  &--progress { border-color: rgba(0, 207, 232, 0.4); .ct-stat__num { color: $ct-info; } }
  &--late { border-color: rgba(234, 84, 85, 0.4); .ct-stat__num { color: $ct-danger; } }
}

.ct-stat__num {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ct-stat__lbl {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary);
  margin-top: 2px;
}

.ct-refresh {
  width: 40px;
  height: 40px;
  border: 1px solid $ct-border;
  border-radius: 12px;
  background: var(--card-bg, white);
  color: $ct-primary;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;

  &:hover {
    background: $ct-primary;
    color: white;
    transform: rotate(180deg);
  }
}

/* ── Filters — professional layout ───────────────────────── */
.ct-filters {
  margin-bottom: 18px;
  background: var(--card-bg, white);
  border: 1px solid $ct-border;
  border-radius: $ct-radius-lg;
  padding: 14px 16px;
}

.ct-filters__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid $ct-border;
}

.ct-filters__chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.ct-filters__body {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 12px;
  align-items: end;
}

.ct-search {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid $ct-border;
  border-radius: 999px;
  padding: 0 12px;
  height: 36px;
  transition: all 0.18s ease;

  &:focus-within {
    border-color: $ct-primary;
    background: rgba(115, 103, 240, 0.04);
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.ct-search__icon {
  color: var(--secondary);
  flex-shrink: 0;
  margin-right: 8px;
}

.ct-search__input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text);
  padding: 0;
  min-width: 0;

  &::placeholder { color: var(--secondary); opacity: 0.7; }
}

.ct-search__clear {
  width: 22px;
  height: 22px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  color: var(--secondary);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;

  &:hover { background: $ct-danger; color: white; }
}

/* Filter chip pills */
.ct-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid $ct-border;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--secondary);
  white-space: nowrap;

  &:hover { transform: translateY(-1px); }

  &--primary {
    color: $ct-primary;
    &.active { background: $ct-primary; border-color: $ct-primary; color: white; box-shadow: 0 4px 12px rgba(115, 103, 240, 0.32); }
  }
  &--warning {
    color: $ct-warning;
    &.active { background: $ct-warning; border-color: $ct-warning; color: white; box-shadow: 0 4px 12px rgba(255, 159, 67, 0.32); }
  }
  &--info {
    color: $ct-info;
    &.active { background: $ct-info; border-color: $ct-info; color: white; box-shadow: 0 4px 12px rgba(0, 207, 232, 0.32); }
  }
  &--danger {
    color: $ct-danger;
    &.active { background: $ct-danger; border-color: $ct-danger; color: white; box-shadow: 0 4px 12px rgba(234, 84, 85, 0.32); }
  }
}

.ct-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  font-size: 0.66rem;
  font-weight: 800;
}

.ct-chip.active .ct-chip__count {
  background: rgba(255, 255, 255, 0.22);
  color: white;
}

.ct-clear-btn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  border: 1px dashed $ct-danger;
  border-radius: 999px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  color: $ct-danger;
  cursor: pointer;
  transition: all 0.18s ease;
  align-self: end;

  &:hover { background: $ct-danger; color: white; }
}

.ct-filter-field label {
  display: flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary);
  margin-bottom: 6px;
}

/* ── Loading + empty ──────────────────────────────────── */
.ct-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--secondary);
  font-weight: 600;
}

.ct-empty {
  text-align: center;
  padding: 60px 20px;
  background: var(--card-bg, white);
  border: 1.5px dashed $ct-border;
  border-radius: $ct-radius-lg;

  h4 { font-weight: 700; margin: 12px 0 4px; font-size: 1.1rem; }
  p { color: var(--secondary); font-size: 0.9rem; margin: 0; }
}

.ct-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(40, 199, 111, 0.1);
  color: $ct-success;

  i { font-size: 1.6rem; }
}

/* ── Grid of property groups ──────────────────────────── */
.ct-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 14px;
}

.ct-group {
  background: var(--card-bg, white);
  border: 1px solid $ct-border;
  border-radius: $ct-radius-lg;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ct-group__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid $ct-border;
  background: linear-gradient(180deg, rgba(115, 103, 240, 0.04) 0%, transparent 100%);
}

.ct-group__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.9rem;

  &--building {
    background: rgba(115, 103, 240, 0.12);
    color: $ct-primary;
  }
  &--house {
    background: rgba(0, 207, 232, 0.12);
    color: $ct-info;
  }
}

.ct-group__info { flex: 1; min-width: 0; }

.ct-group__addr {
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ct-group__count {
  font-size: 0.72rem;
  color: var(--secondary);
  font-weight: 600;
}

/* ── Task card (inside group) ─────────────────────────── */
.ct-tasks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
}

.ct-task {
  position: relative;
  text-align: left;
  background: var(--card-bg, white);
  border: 1px solid $ct-border;
  border-radius: $ct-radius;
  padding: 12px 14px 12px 18px;
  cursor: pointer;
  transition: all 0.18s ease;
  width: 100%;
  font-family: inherit;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(34, 41, 47, 0.08);
    border-color: rgba(115, 103, 240, 0.3);
  }

  &--urgent { background: rgba(255, 159, 67, 0.04); }
  &--late { background: rgba(234, 84, 85, 0.04); }
  &--progress { background: rgba(0, 207, 232, 0.04); }
}

.ct-task__stripe {
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 3px;
  border-radius: 999px 0 0 999px;
  background: $ct-primary;

  .ct-task--urgent & { background: $ct-warning; }
  .ct-task--progress & { background: $ct-info; }
  .ct-task--late & { background: $ct-danger; }
}

.ct-task__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}

.ct-task__apt-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
}

.ct-task__apt {
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  text-transform: capitalize;
}

.ct-task__rel {
  font-size: 0.7rem;
  color: var(--secondary);
  display: inline-flex;
  align-items: center;
}

.ct-task__status {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 9px;
  flex-shrink: 0;
}

.ct-task__row {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  margin-bottom: 4px;
  color: var(--text);
}

.ct-task__row-icon {
  color: var(--secondary);
  flex-shrink: 0;
  margin-right: 6px;
}

.ct-task__link {
  color: var(--text);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  &:hover { color: $ct-primary; }
}

.ct-task__client { font-weight: 600; }
.ct-task__time { color: var(--secondary); }

.ct-task__notes {
  font-size: 0.74rem;
  color: var(--secondary);
  margin: 6px 0 0 0;
  font-style: italic;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ct-task__alert {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  font-size: 0.7rem;
  font-weight: 700;
  color: $ct-danger;
  background: rgba(234, 84, 85, 0.1);
  padding: 3px 8px;
  border-radius: 999px;
}

.ct-task__foot {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.ct-task__btn {
  width: 28px;
  height: 28px;
  border: 1px solid $ct-border;
  border-radius: 8px;
  background: transparent;
  color: var(--secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: $ct-primary;
    border-color: $ct-primary;
    color: white;
  }
}

/* ── Dark mode ──────────────────────────────────────────── */
.dark-layout {
  // Surfaces flip to navy card colour
  .ct-hero {
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.18) 0%, rgba(0, 207, 232, 0.10) 100%);
    border-color: $ct-border-dark;
  }
  .ct-filters,
  .ct-empty,
  .ct-group,
  .ct-task,
  .ct-stat,
  .ct-refresh,
  .ct-task__btn { border-color: $ct-border-dark; }

  .ct-filters,
  .ct-empty,
  .ct-group,
  .ct-task,
  .ct-stat,
  .ct-refresh { background: #283046; }

  // Group header gradient — make subtle on dark
  .ct-group__head {
    border-bottom-color: $ct-border-dark;
    background: linear-gradient(180deg, rgba(115, 103, 240, 0.14) 0%, transparent 100%);
  }

  // Status-tinted backgrounds use dark variants
  .ct-task--urgent { background: rgba(255, 159, 67, 0.08); }
  .ct-task--late { background: rgba(234, 84, 85, 0.08); }
  .ct-task--progress { background: rgba(0, 207, 232, 0.08); }

  .ct-task:hover {
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.4);
    border-color: rgba(115, 103, 240, 0.5);
  }

  .ct-task__btn {
    color: rgba(255, 255, 255, 0.6);
    background: transparent;
  }

  // ── TEXT ON CARDS — explicit overrides so the tinted card backgrounds
  //   don't leave dark body text unreadable in dark mode ──
  .ct-task__apt { color: rgba(255, 255, 255, 0.96) !important; }
  .ct-task__client { color: rgba(255, 255, 255, 0.95) !important; font-weight: 700; }
  .ct-task__time { color: rgba(255, 255, 255, 0.6) !important; }
  .ct-task__rel { color: rgba(255, 255, 255, 0.65) !important; }
  .ct-task__notes { color: rgba(255, 255, 255, 0.7) !important; }
  .ct-task__row { color: rgba(255, 255, 255, 0.85); }
  .ct-task__row-icon { color: rgba(255, 255, 255, 0.55); }
  .ct-group__addr { color: rgba(255, 255, 255, 0.95); }
  .ct-group__count { color: rgba(255, 255, 255, 0.55); }
  .ct-hero__title { color: rgba(255, 255, 255, 0.98); }
  .ct-hero__subtitle { color: rgba(255, 255, 255, 0.65); }

  // Stat labels + filter labels
  .ct-filter-field label,
  .ct-stat__lbl { color: rgba(255, 255, 255, 0.55); }

  // Filter UI on dark
  .ct-filters__head { border-bottom-color: $ct-border-dark; }
  .ct-search {
    background: rgba(255, 255, 255, 0.04);
    border-color: $ct-border-dark;
    &:focus-within { background: rgba(115, 103, 240, 0.1); }
  }
  .ct-search__input { color: rgba(255, 255, 255, 0.95); }
  .ct-search__clear { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.6); }

  .ct-chip {
    border-color: $ct-border-dark;
    background: rgba(255, 255, 255, 0.02);
  }
  .ct-chip__count { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.7); }
  .ct-chip.active .ct-chip__count { background: rgba(255, 255, 255, 0.22); color: white; }

  // Task link colour for visibility
  .ct-task__link { color: rgba(255, 255, 255, 0.92); }
  .ct-task__link:hover { color: $ct-primary; }

  // Empty state
  .ct-empty h4 { color: rgba(255, 255, 255, 0.95); }
  .ct-empty p { color: rgba(255, 255, 255, 0.6); }
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 700px) {
  .ct-hero {
    flex-direction: column;
    align-items: stretch;
  }
  .ct-hero__stats {
    justify-content: space-between;
  }
  .ct-filters__head {
    flex-direction: column;
    align-items: stretch;
  }
  .ct-filters__body {
    grid-template-columns: 1fr;
  }
  .ct-clear-btn { width: 100%; justify-content: center; }
}
</style>
